import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface ProcessExecution {
  _id: string;
  job_key: string;
  job_label?: string;
  project: string;
  trigger: 'cron' | 'manual_console' | 'manual_api' | 'change_stream' | 'internal' | 'retry';
  triggered_by?: any;
  status: 'queued' | 'running' | 'success' | 'partial' | 'failed' | 'cancelled';
  params?: any;
  progress?: { total?: number; processed?: number; ok?: number; failed?: number; skipped?: number; current_batch?: number; eta_ms?: number };
  summary?: any;
  started_at?: string;
  finished_at?: string;
  duration_ms?: number;
  heartbeat_at?: string;
  cancel_requested?: boolean;
  error_message?: string;
  stale?: boolean;
  eta_ms?: number | null;
  /**
   * Que campo llena cada casilla del panel. Lo manda el backend resuelto desde
   * services/process-tracker/registry.js, porque cada job nombra sus contadores
   * distinto (total_universo / totalUniverse / total, created / generated /
   * deactivated, skipped / unchanged / kept) y unos escriben en `progress` y otros
   * en `summary`.
   *
   * Cada valor es una ruta ('summary.kept'), un arreglo de rutas que se suman
   * (['summary.updated','summary.deactivated']), o null si el job no reporta ese dato.
   */
  summary_map?: {
    escaneados?: string | string[] | null;
    recorridos?: string | string[] | null;
    procesados?: string | string[] | null;
    sin_cambio?: string | string[] | null;
    fallidos?: string | string[] | null;
  } | null;
  /** Definicion del job desde el registro. Solo viene en el endpoint de detalle. */
  job_definition?: JobDefinition & { summary_map?: ProcessExecution['summary_map'] };
}

/** Lo que se pinta de una corrida: las cuatro casillas y el porcentaje. */
export interface CasillasProceso {
  escaneados: number | null;
  aProcesar: number | null;
  procesados: number | null;
  sinCambio: number | null;
  fallidos: number | null;
  pct: number;
  sinMapa: boolean;
}

/** Resuelve una ruta tipo 'summary.kept' o ['summary.a','summary.b'] (que se suman). */
function valorDe(run: any, ruta: string | string[] | null | undefined): number | null {
  if (!ruta || !run) return null;
  if (Array.isArray(ruta)) {
    const partes = ruta.map(r => valorDe(run, r)).filter(v => v !== null) as number[];
    return partes.length ? partes.reduce((a, b) => a + b, 0) : null;
  }
  const v = ruta.split('.').reduce((o: any, k: string) => (o == null ? o : o[k]), run);
  return typeof v === 'number' ? v : null;
}

/**
 * La UNICA cuenta de las casillas y del porcentaje. La usan la lista y el detalle.
 *
 * Vive acá y no en un componente porque ya paso: se arreglo el porcentaje en el detalle
 * y la lista siguio mostrando 97% con el cartel de "success" al lado, porque tenia su
 * propia copia del calculo. Una pantalla nueva debe llamar a esto, no reescribirlo.
 *
 * El porcentaje mide sobre lo que HABIA QUE AJUSTAR, no sobre lo escaneado: un job que
 * reviso todo y no encontro nada que cambiar hizo su trabajo completo.
 *
 * Y si termino en success es 100% aunque el cociente diga otra cosa: `progress.total` se
 * calcula ANTES de recorrer y para cuando el job termina ya vencio (entran pagos, salen
 * cuotas del rango). Medido en staging el 2026-08-13: 104.102/106.843 = 97% con la corrida
 * cerrada en success y cero fallidos.
 */
export function resolverCasillas(run: ProcessExecution | null | undefined): CasillasProceso {
  const vacio: CasillasProceso = { escaneados: null, aProcesar: null, procesados: null,
                                   sinCambio: null, fallidos: null, pct: 0, sinMapa: true };
  if (!run) return vacio;

  const mapa = run.summary_map || run.job_definition?.summary_map || null;
  const escaneados = valorDe(run, mapa?.escaneados) ?? (run.progress?.total ?? null);
  const procesados = valorDe(run, mapa?.procesados);
  const sinCambio  = valorDe(run, mapa?.sin_cambio);
  const fallidos   = valorDe(run, mapa?.fallidos) ?? (run.progress?.failed ?? null);

  // Si el job reporta sin_cambio, el universo a procesar es lo ajustado + lo que no hizo
  // falta ajustar. Si no lo reporta, se cae al escaneado.
  const aProcesar = procesados === null ? null
    : (sinCambio !== null ? procesados + sinCambio : (escaneados ?? procesados));

  let pct: number;
  if (run.status === 'success') {
    pct = 100;
  } else if (procesados !== null && aProcesar !== null && aProcesar > 0) {
    pct = Math.min(100, Math.round((procesados / aProcesar) * 100));
  } else {
    const p = run.progress;
    pct = p?.total ? Math.min(100, Math.round(((p.processed || 0) / p.total) * 100)) : 0;
  }

  return { escaneados, aProcesar, procesados, sinCambio, fallidos, pct, sinMapa: !mapa };
}

export interface JobDefinition {
  job_key: string;
  label: string;
  category: string;
  project: string;
  manual_trigger_url?: string | null;
  item_columns?: Array<{ key: string; header: string }>;
}

@Injectable({ providedIn: 'root' })
export class ProcessExecutionsService {
  private base = `${environment.BASE_LA}/api/admin/process-executions`;

  constructor(private http: HttpClient) {}

  listJobs() {
    return lastValueFrom(this.http.get<{ ok: boolean; jobs: JobDefinition[] }>(`${this.base}/jobs`));
  }

  list(filters: { job_key?: string; status?: string; project?: string; trigger?: string; from?: string; to?: string; page?: number; limit?: number } = {}) {
    let params = new HttpParams();
    Object.entries(filters).forEach(([k, v]) => { if (v != null && v !== '') params = params.set(k, String(v)); });
    return lastValueFrom(this.http.get<{ ok: boolean; page: number; limit: number; total: number; items: ProcessExecution[] }>(this.base, { params }));
  }

  get(runId: string) {
    return lastValueFrom(this.http.get<{ ok: boolean; run: ProcessExecution; job_definition: JobDefinition | null }>(`${this.base}/${runId}`));
  }

  progress(runId: string) {
    return lastValueFrom(this.http.get<ProcessExecution & { ok: boolean; stale: boolean }>(`${this.base}/${runId}/progress`));
  }

  events(runId: string, level?: string, page = 1, limit = 100) {
    let params = new HttpParams().set('page', String(page)).set('limit', String(limit));
    if (level) params = params.set('level', level);
    return lastValueFrom(this.http.get<{ ok: boolean; total: number; items: any[] }>(`${this.base}/${runId}/events`, { params }));
  }

  items(runId: string, result?: string, page = 1, limit = 50) {
    let params = new HttpParams().set('page', String(page)).set('limit', String(limit));
    if (result) params = params.set('result', result);
    return lastValueFrom(this.http.get<{ ok: boolean; total: number; items: any[] }>(`${this.base}/${runId}/items`, { params }));
  }

  downloadDetailXlsxUrl(runId: string): string {
    return `${this.base}/${runId}/detail.xlsx`;
  }

  cancel(runId: string) {
    return lastValueFrom(this.http.post<{ ok: boolean; run: ProcessExecution }>(`${this.base}/${runId}/cancel`, {}));
  }

  delete(runId: string) {
    return lastValueFrom(this.http.delete<{ ok: boolean; deleted: { run: number; items: number; events: number } }>(`${this.base}/${runId}`));
  }

  trigger(jobKey: string, body: any = {}) {
    return lastValueFrom(this.http.post<{ ok: boolean; forwarded_to: string; response: any }>(`${this.base}/run/${jobKey}`, body));
  }
}
